// `courses-devices` (spec 6.6, 6.7): the member's Next Steps encryption status
// and the devices attached to it. It is what the two approval screens read —
// "Approve this device" on a device with no key, "New device request" on one
// that has it — and the only place in the app that spends a challenge nonce.
//
// It holds NO key material. Every signature and every wrapping arrives from the
// caller: `courses-crypto` owns MK, the device key pair and the bundle signing
// key (#1539), and `courses-keys` owns where they live. A Vuex module is the
// wrong place to unwrap a key, and keeping the boundary here means this module
// can be reasoned about — and tested — without any crypto at all.
//
// Two rules of the deployed API shape everything below:
//
//  1. **A challenge is spent on any attempt**, verified or not
//     (signed-operation.guard.ts). So every signed action goes through
//     `withFreshChallenge`: challenge, sign, call, and exactly one retry with a
//     fresh challenge on `stale-nonce`. Two signed calls never share a nonce.
//  2. **`register` never upgrades an existing row.** The member's own device id
//     answers 201 with the row as it already is, whatever its status, so the
//     truth after a registration is the status read that follows it — not the
//     row on the wire. A `REVOKED` id is never upgraded at all: a denied device
//     has to come back under a new identity (`newDeviceId()`).
//
// Two error fields, because they answer different questions:
//
//  - `state.error` is the last failure of an action the MEMBER took. Every
//    action they can take records its failure there — including the four
//    argument guards, under `CLIENT_GUARD` — and RETHROWS, so a screen can
//    both render a sentence and branch on the throw. A `stale-nonce` that
//    survived its retry stays `retryable: true`: the member taps Retry, they
//    do not watch a spinner. Reads never write it and never clear it: the poll
//    runs every four seconds, and an error that vanished mid-read (with its
//    Try again button) would be worse than none.
//  - `state.readError` is the last failure of a status read — the poll's own
//    ticks and the read that follows every mutating action. Nothing is silent,
//    and a successful approve whose follow-up read failed is still a
//    successful approve.
//
// Screens render `state.error` through `errorCopy(code)`
// (`pages/courses/encryption/error-copy.js`), never `error.message`: the
// message is the API's developer diagnostic.
//
// `state.epoch` is what makes all of this safe across a sign-out. Logout
// commits `reset` and then AWAITS `courses-keys/wipe` and the Auth0 redirect
// (`mixins/Logout.js`), a window in which a GET issued for the previous member
// resolves; clearing the interval stops only the ticks that have not started.
// Every read captures the epoch it began in and commits nothing once it has
// moved, so the next member on a shared phone cannot boot with the previous
// member's device list in Vuex.
//
// ⚠️ NOT handled here: nothing serialises two signed operations. The API keeps
// ONE live nonce per member and `issue` replaces any existing one
// (`challenge-store.ts:36-40, 59-63`), so two signed calls overlapping — two
// pending devices approved in quick succession (#1547), an approve overlapping
// a recovery write — make the loser fail as a non-retryable
// `signature-rejected`. A promise chain here would fix that but would also let
// one hung request wedge every later signed operation for ever: this repo sets
// no axios timeout anywhere. Until there is one, a caller that can issue two
// signed operations at once has to serialise them itself (both screens already
// take `busy` from their caller for exactly this reason).
import {
  approveDevice as approveDeviceRequest,
  cancelReset as cancelResetRequest,
  denyDevice as denyDeviceRequest,
  DEVICE_STATUSES,
  fetchEncryptionStatus,
  isEncryptionApiError,
  markMigrated as markMigratedRequest,
  fetchRotationRows,
  registerDevice,
  removePassphrase as removePassphraseRequest,
  requestReset as requestResetRequest,
  revokeDevice as revokeDeviceRequest,
  rotateAbort as rotateAbortRequest,
  rotateAbortBegin as rotateAbortBeginRequest,
  rotateBegin as rotateBeginRequest,
  rotateCommit as rotateCommitRequest,
  setPassphrase as setPassphraseRequest,
  setRecovery as setRecoveryRequest,
} from '@/utils/courses-encryption-api';
import { withFreshChallenge } from '@/utils/courses-encryption/signed-operation';
import { trackElapsedSince } from '@/utils/courses-encryption/action-timing';

/** How often the waiting device asks whether an approved one has answered. */
export const DEFAULT_POLL_INTERVAL_MS = 4000;

/** `thisDeviceStatus` when the API knows nothing about this install yet. */
export const UNREGISTERED = 'unregistered';

/**
 * `error.code` for a call this module refused locally rather than sending.
 * Deliberately not one of `ENCRYPTION_ERROR_CODES`: a screen has to be able to
 * tell a refusal this device made from a real server failure, and `errorCopy`
 * has no sentence for it, so the member reads the generic one instead of a
 * developer sentence.
 */
export const CLIENT_GUARD = 'client-guard';

const initialState = () => ({
  /** The last `GET /api/me/encryption` snapshot, verbatim, or null. */
  status: null,
  /** This install's registrable identity: `{ id, publicKey, name, platform }`. */
  localDevice: null,
  /** True while a status read is in the air. Reads only; see `startPolling`. */
  loading: false,
  /** The member's last action: `{ code, message, status, retryable, route }`, or null. */
  error: null,
  /** The last status read's failure, same shape. Never what a member's tap did. */
  readError: null,
  polling: false,
  pollTimer: null,
  /**
   * A request the member asked to see again, from the Devices screen (DEV-15,
   * 2026-09-11). The request host clears its "Not now" for it and presents.
   * Consumed by the host; null in between.
   */
  reviewRequestId: null,
  lastRefreshedAt: null,
  /**
   * The plaintext migration's progress, for the Notes Hub header line (#1545,
   * spec 6.7). It lives beside `status` because it is the story of this
   * member's `migratedAt` becoming set, and it is the one part of that story
   * the server cannot tell: `total` is what a collect pass found, `done` is how
   * many of them have been sealed so far. Reset to zeros whenever no run is in
   * flight, so a stale count can never paint a line.
   */
  migration: { running: false, done: 0, total: 0 },
  /**
   * Bumped by `reset`. A read captures it and commits nothing once it has
   * moved, so a request in the air at sign-out cannot write the previous
   * member's snapshot back into a store the next member is about to use.
   */
  epoch: 0,
  /**
   * This device could not overwrite its platform copy of a key that now opens
   * nothing (#1608). Set by the launch watcher after a reconciliation, and read
   * by the Devices screen — the one place in the app where a member is looking
   * at what this phone still holds. Not an `error`: nothing failed that the
   * member asked for, and there is no Retry to offer.
   */
  platformCopyStuck: false,
  /**
   * The pending-reset notice is on screen right now (#1691). Set by
   * `ResetPendingNoticeHost` as it presents and dismisses, and read by
   * `PendingDeviceRequestHost`, which stays quiet while it is true.
   *
   * BOTH ARE APP-SHELL MODALS ON EVERY ROUTE, which is why this exists at all:
   * a member can have a device waiting AND a reset running, and until #1691
   * they could not collide because the request was gated on two Next Steps
   * routes. Now that it is not, something has to order them, and the
   * destructive one goes first — a member reading "everything you have written
   * will be deleted" must not have it covered by a request about a phone.
   *
   * IN VUEX, not read off the DOM or duplicated as a predicate: the notice's
   * "is it due" answer folds in a `localStorage` dismissal, which is not
   * reactive, so a second component recomputing it would not re-evaluate when
   * the member taps "This was me". One writer, one reader, one reactive fact.
   */
  resetNoticeOnScreen: false,
});

/**
 * The status read in the air, per module state (so two stores in one test
 * never share one). Not in Vuex state: a pending promise is not a snapshot.
 *
 * @type {WeakMap<object, { epoch: number, read: Promise<object> }>}
 */
const inFlightReads = new WeakMap();

/**
 * A plain, serialisable failure a component can bind straight to a prop.
 *
 * `courses` stores a bare message string; this one keeps `code` and `retryable`
 * because the screens have to tell "tap Retry" apart from "this will never
 * work", and that distinction is the whole point of the error codes.
 *
 * @param {unknown} error
 * @returns {{ code: string, message: string, status: number|null, retryable: boolean, route: string|null }}
 */
function errorSnapshot(error) {
  if (isEncryptionApiError(error)) {
    // `apiCode` travels with it. `code` collapses several API codes into one —
    // every 409 becomes CONFLICT — and `courses-encryption-api.js` names
    // `apiCode` as what a screen reads when it needs the single case. Dropping
    // it here left screens unable to tell "another device wrote first, try
    // again" from "a rotation is holding this, and retrying cannot work".
    const { code, apiCode, message, status, retryable, route } = error;
    return { code, apiCode, message, status, retryable, route };
  }
  return {
    code: 'unknown',
    apiCode: null,
    message: String(error?.message ?? error),
    status: null,
    retryable: false,
    route: null,
  };
}

/**
 * Refuse a call this module will not make, recording it the way an API failure
 * is recorded so a screen bound to `state.error` says something. These fire
 * before any request, and part 2's signer closes over a key that is unwrapped
 * asynchronously — `signer: undefined` is a real runtime path, not only a
 * coding slip, and it used to leave the member with a dead button and no
 * explanation at all.
 *
 * @param {import('vuex').ActionContext} context
 * @param {string} message
 * @returns {never}
 */
function refuse({ commit }, message) {
  commit('setError', {
    code: CLIENT_GUARD,
    message,
    status: null,
    retryable: false,
    route: null,
  });
  throw new Error(message);
}

/**
 * The `auth` every client function takes. `deviceId` is null until this install
 * has minted one: the API treats a non-UUID `X-Device-Id` as absent, so an
 * empty string would buy nothing and the header is simply omitted.
 *
 * @param {object} rootState
 * @param {ReturnType<typeof initialState>} state
 */
function authFor(rootState, state) {
  return { token: rootState.user.token, deviceId: state.localDevice?.id ?? null };
}

/**
 * Run one call the member asked for, recording its failure in `state.error`
 * and rethrowing it.
 *
 * The previous error is cleared on ENTRY, not on success: a member who taps
 * again sees the old sentence go the moment they do, and nothing else — a
 * poll's successful tick, the follow-up read after this one — can clear a
 * sentence they are still reading.
 *
 * @template T
 * @param {import('vuex').ActionContext} context
 * @param {() => Promise<T>} call
 * @returns {Promise<T>}
 */
async function tracked({ commit }, call) {
  commit('setError', null);
  try {
    return await call();
  } catch (error) {
    commit('setError', errorSnapshot(error));
    throw error;
  }
}

/**
 * A signed operation: a fresh challenge, the caller's signature over it, the
 * call — carrying the signature AND the nonce it covers — and one retry on a
 * stale nonce.
 *
 * The `signer` is `(nonce, { op, params }) => Promise<base64 signature>`.
 * `params` are the REQUEST parameters, not the canonical `fields`: hashing a
 * wrapping needs `courses-crypto`, which this module deliberately does not
 * import. The signer owes the contract's exact field names and types
 * (contract section 5, `encryption.controller.ts`):
 *
 * | op         | `params` handed over                       | canonical `fields` to sign            |
 * | ---------- | ------------------------------------------ | ------------------------------------- |
 * | `register` | `{ deviceId, publicKey, selfWrapped? }`    | `{ deviceId, publicKey }`, plus `selfWrappedHash` when `selfWrapped` is sent (api#1092) |
 * | `approve`  | `{ deviceId, wrappedMk }`                  | `{ deviceId, wrappedMkHash }`         |
 * | `revoke`   | `{ deviceId }`                             | `{ deviceId }`                        |
 * | `recovery` | `{ recoveryWrapping, expectedVersion }`    | `{ recoveryWrappingHash, expectedVersion }` |
 * | `reset`    | `{}`                                       | `{}`                                  |
 * | `cancel`   | `{}`                                       | `{}`                                  |
 *
 * `register` has TWO field sets and the BODY chooses, not the op: the
 * controller's callback is a ternary on `body.selfWrapped`. That is why
 * `registerThisDevice` puts the wrapping into `params` as well as into the
 * body — `params` is the only channel the signer has, and a signature that
 * omitted the hash would cover a message the server never builds.
 *
 * `wrappedMkHash` and `selfWrappedHash` are both `await wrappedMkHash(wrapping)`
 * and `recoveryWrappingHash` is `await recoveryWrappingHash(recoveryWrapping)`,
 * all from `courses-crypto`. `memberId` is supplied by the signer and MUST be the
 * NUMBER: `canonicalMessage` stringifies it unchanged, so `"memberId":"568"`
 * is a different message from the contract's `"memberId":568` and every signed
 * call fails as a 403 with nothing in the error to say why.
 *
 * @template T
 * @param {import('vuex').ActionContext} context
 * @param {{ op: string, params?: object, signer: Function }} operation
 * @param {(signature: string, nonce: string) => Promise<T>} call — the nonce the signature
 *   covers travels in the body too (contract: api PR #1092), so `call` receives both
 * @returns {Promise<T>}
 */
function signed(context, { op, params = {}, signer }, call) {
  if (typeof signer !== 'function') {
    refuse(context, `courses-devices/${op}: a signer is required to prove possession of the key`);
  }
  const auth = authFor(context.rootState, context.state);
  return tracked(context, () => withFreshChallenge(auth, signer, call, { op, params }));
}

export default {
  namespaced: true,
  state: initialState,

  getters: {
    enrolled: state => state.status?.enrolled === true,
    /** The API's own view of this install, the only one carrying `wrappedMk`. */
    thisDevice: state => state.status?.thisDevice ?? null,
    /** @returns {'unregistered'|'PENDING'|'APPROVED'|'REVOKED'} */
    thisDeviceStatus: state => state.status?.thisDevice?.status ?? UNREGISTERED,
    /**
     * The requests to show on an approved device. This install's own row is
     * excluded: on the screen that is itself waiting, it would render as a
     * request to approve yourself.
     */
    pendingDevices: state =>
      (state.status?.devices ?? []).filter(
        device => device.status === DEVICE_STATUSES.PENDING && device.id !== state.localDevice?.id,
      ),
    /** The devices that can grant approval — what screen 2's list must show. */
    approvedDevices: state =>
      (state.status?.devices ?? []).filter(device => device.status === DEVICE_STATUSES.APPROVED),
    hasRecoveryCode: state => state.status?.hasRecoveryCode === true,
    pendingReset: state => state.status?.pendingReset ?? null,
    /** The Hub header's progress line, or nothing while no run is in flight. */
    migrationProgress: state => (state.migration.running ? state.migration : null),
    /** The retention promise the server computes for this member (spec 6.11). */
    promise: state => state.status?.promise ?? null,
    /**
     * Every device that holds the key or is waiting for it, this install's row
     * included — what the Devices screen lists. `pendingDevices` and
     * `approvedDevices` above answer the approval screens' narrower questions
     * and both drop rows this one has to show. A denied or removed device is
     * not one of them: the status carries `REVOKED` rows for ever, and a screen
     * headed "these devices can read your notes" must not list one.
     */
    devices: state =>
      (state.status?.devices ?? []).filter(device => device.status !== DEVICE_STATUSES.REVOKED),
    /** The member's own migration date, which the promise line reads with `promise`. */
    migratedAt: state => state.status?.migratedAt ?? null,
    /**
     * What a recovery write must compare-and-set against. `PUT /recovery`
     * answers 409 when this has moved, which is what stops two devices racing
     * the wrapping — so a screen that guesses it defeats the check.
     */
    recoveryVersion: state => state.status?.recoveryVersion ?? 0,
    /**
     * MK wrapped under the member's recovery code, `{ salt, iv, wrapped }`, or
     * null while they have never saved one. It is served to any signed-in
     * session and is useless without the code, which is why the code is the
     * only thing the app never stores.
     */
    recoveryWrapping: state => state.status?.recoveryWrapping ?? null,
    /** Whether the member has set the optional passphrase (#1695). */
    hasPassphrase: state => state.status?.hasPassphrase === true,
    /**
     * What a passphrase write must compare-and-set against, exactly as
     * `recoveryVersion` is for the code. A screen that guesses it defeats the
     * check that stops a removal silently deleting a set another device just
     * made.
     */
    passphraseVersion: state => state.status?.passphraseVersion ?? 0,
    /**
     * MK under the passphrase-derived KEK, `{ kdf, iterations, salt, iv,
     * wrapped }`, or null while they have never set one. Served to any
     * signed-in session like the recovery wrapping, and useless without the
     * passphrase — which is why the passphrase is never stored anywhere.
     */
    passphraseWrapping: state => state.status?.passphraseWrapping ?? null,
    /** The bundle signing key, wrapped under MK; a signer is built from it. */
    signingKeyWrapped: state => state.status?.signingKeyWrapped ?? null,
    /**
     * The rotation in progress (#1694, spec 6.12), or null:
     * `{ pendingKeyId, startedAt, deviceId, remaining }`. `remaining` counts
     * this member's rows still sealed under the OLD generation, and is what
     * `rotate/commit` refuses on — so it is progress and predicate in one.
     */
    rotation: state => state.status?.rotation ?? null,
    /** The generation a rotation is moving INTO, or null. */
    pendingKeyId: state => state.status?.rotation?.pendingKeyId ?? null,
    /**
     * The pending bundle signing key, wrapped under MK2 (api#1140).
     *
     * Inside `rotation`, not beside it, because it exists only while one is
     * running. Commit and abort are signed with it, and the only device that
     * ever held it in memory is the one that ran begin — so this is what makes
     * a rotation interrupted by a relaunch finishable rather than stranded.
     */
    pendingSigningKeyWrapped: state => state.status?.rotation?.signingKeyWrapped ?? null,
    /**
     * MK2 under the member's NEW recovery code, and under their passphrase
     * (api#1146).
     *
     * The other half of `pendingSigningKeyWrapped`: that one lets a device
     * still holding MK2 resume, and these let a device that has LOST it get
     * MK2 back from the member. Which is the difference between a rotation
     * interrupted by a relaunch and one interrupted by a sign-out.
     */
    pendingRecoveryWrapping: state => state.status?.rotation?.recoveryWrapping ?? null,
    pendingPassphraseWrapping: state => state.status?.rotation?.passphraseWrapping ?? null,
    /**
     * MK1 under the NEW recovery code (api#1147). What makes the code the
     * rotation screen just showed the member open BOTH generations — MK1 to
     * read the rows the walk has not moved, MK2 to write them back.
     */
    pendingPreviousWrapping: state => state.status?.rotation?.previousWrapping ?? null,
    /**
     * The rotation is being UNWOUND, not completed (api#1148).
     *
     * ⚠️ MUST BE READ BEFORE `rotationRemaining`. The same number means opposite
     * things in the two states — forwards it counts rows still on the committed
     * generation, while aborting it counts rows still on the pending one — so a
     * walk that read the count without this would re-seal in the direction the
     * rotation is leaving, and never finish.
     */
    rotationAborting: state => state.status?.rotation?.aborting === true,
    /** How many of this member's rows are still on the old generation. */
    rotationRemaining: state => state.status?.rotation?.remaining ?? 0,
    /** The member's key id, as every wrapping was made under. */
    keyId: state => state.status?.keyId ?? null,
    /** MK wrapped to this device, ready for `unwrapMasterKeyFromDevice`. */
    wrappedMkForThisDevice: state => state.status?.thisDevice?.wrappedMk ?? null,
    /**
     * Enrolled elsewhere and this device cannot read anything yet: it has never
     * registered, or it is still waiting. A `REVOKED` device is not waiting for
     * anything — it was refused, or signed out afterwards, and needs a new
     * identity either way.
     */
    needsApproval: (state, getters) =>
      getters.enrolled &&
      (getters.thisDeviceStatus === UNREGISTERED ||
        getters.thisDeviceStatus === DEVICE_STATUSES.PENDING),
    /** This install's id once `courses-keys` has published it; null until then. */
    localDeviceId: state => state.localDevice?.id ?? null,
    /**
     * The devices that can approve THIS one: APPROVED, and not this install.
     * Its own row does not count whatever its status — APPROVED with nothing
     * wrapped to it is still waiting on another device. While the local id is
     * unknown nothing can be excluded, so every approved row is taken as
     * another device; that resolves itself once the identity is published.
     */
    otherApprovedDevices: (state, getters) =>
      getters.approvedDevices.filter(device => device.id !== getters.localDeviceId),
    /**
     * #1652, spec 6.7 "Enrolment is only ever additive": this phone holds no
     * key, the member is enrolled, no other device is approved and no recovery
     * code is saved, once a status read has landed. Not "enrolled elsewhere"
     * but the stranded state, which decision 40's rules make unreachable — so
     * the approval screen names it, offers Try again, and never presents Start
     * over as the only exit. The key is the one fact this module does not own,
     * and it comes first: a phone that holds the key IS the approver, whatever
     * the list says, and the launch check must not call a healthy one-phone
     * member stranded. Before a read has landed an empty list is "not fetched
     * yet", not "nobody".
     *
     * ⚠️ `isHydrated` GUARDS `hasMasterKey`, and is not redundant with
     * `lastRefreshedAt`. The two are separate races: the status read is HTTP,
     * the key read is the platform key store, and on native the Keychain or
     * Keystore round trip can easily lose to it. Until `courses-keys` has
     * hydrated, `hasMasterKey` is false because nothing has been read yet, not
     * because there is no key — so a device that holds the key and is the only
     * approver reads as stranded for exactly as long as that gap. That would
     * flash "This phone has no key for your notes" at a healthy member, and
     * `installStrandedStateCheck` latches per member per session, so the false
     * report would also be the ONLY one: the real occurrence, later in the
     * session, would be swallowed by the same latch.
     */
    stranded: (state, getters, rootState, rootGetters) =>
      rootGetters['courses-keys/isHydrated'] === true &&
      rootGetters['courses-keys/hasMasterKey'] !== true &&
      !!state.lastRefreshedAt &&
      getters.enrolled &&
      !getters.hasRecoveryCode &&
      // A passphrase is a way back in too (#1695, decision 11). Without this a
      // member who set one instead of saving a code would be told "this phone
      // has no key for your notes" and pushed towards Start over — which
      // erases everything — while holding a recovery path that works.
      !getters.hasPassphrase &&
      getters.otherApprovedDevices.length === 0,
    /** See the state field: a dead key this phone could not overwrite (#1608). */
    platformCopyStuck: state => state.platformCopyStuck === true,
    /** See the state field: the reset notice holds the screen first (#1691). */
    resetNoticeOnScreen: state => state.resetNoticeOnScreen === true,
  },

  mutations: {
    /**
     * Back to the initial state, timer included — except `epoch`, which is the
     * one field that must move FORWARD rather than back.
     *
     * Clearing the interval here is not an unrelated side effect: `pollTimer` is
     * this module's state, and a handle dropped without being cleared keeps
     * firing. Logout commits this (mixins/Logout.js), and a poll surviving it
     * would ask for the previous member's encryption status with their token.
     *
     * Clearing the interval is not enough on its own, though: it stops the
     * ticks that have not started, not the GET already in the air. The epoch is
     * what stops that one from committing (see `refreshStatus`).
     */
    reset(state) {
      if (state.pollTimer !== null) clearInterval(state.pollTimer);
      const epoch = state.epoch + 1;
      Object.assign(state, initialState(), { epoch });
    },
    /** @param {{ id: string, publicKey: string, name: string, platform: string }|null} device */
    setLocalDevice(state, device) {
      state.localDevice = device
        ? {
            id: device.id,
            publicKey: device.publicKey,
            name: device.name,
            platform: device.platform,
          }
        : null;
    },
    setStatus(state, status) {
      state.status = status;
      state.lastRefreshedAt = Date.now();
    },
    /**
     * Merge a patch into the migration's progress. A new object rather than a
     * mutated one, so a component watching `migration` sees the change.
     *
     * @param {{ running?: boolean, done?: number, total?: number }} patch
     */
    setMigration(state, patch) {
      state.migration = { ...state.migration, ...patch };
    },
    setLoading(state, loading) {
      state.loading = !!loading;
    },
    setError(state, error) {
      state.error = error;
    },
    /** A read's own failure. Never what the member's last tap did. */
    setReadError(state, error) {
      state.readError = error;
    },
    setPolling(state, polling) {
      state.polling = !!polling;
    },
    /** @param {string|null} id */
    setReviewRequest(state, id) {
      state.reviewRequestId = typeof id === 'string' && id ? id : null;
    },
    /** @param {boolean} stuck */
    setPlatformCopyStuck(state, stuck) {
      state.platformCopyStuck = !!stuck;
    },
    /** @param {boolean} onScreen */
    setResetNoticeOnScreen(state, onScreen) {
      state.resetNoticeOnScreen = !!onScreen;
    },
    setPollTimer(state, timer) {
      state.pollTimer = timer;
    },
  },

  actions: {
    /**
     * This install's registrable identity. In practice it is fed from
     * `courses-keys/deviceIdentity` (the persisted id and public key) plus
     * `describeThisDevice()` (the label and platform); only the four fields a
     * register body carries are kept, so a private key handed in by mistake is
     * dropped rather than stored in Vuex.
     */
    setLocalDevice({ commit }, device) {
      commit('setLocalDevice', device);
    },

    /**
     * `GET /api/me/encryption`. Never 404s: an unenrolled member answers
     * `{ enrolled: false, ... }`, so this can be called unconditionally.
     *
     * Single-flight: a second caller — a screen's own read landing while the
     * poll's tick is out — joins the request in the air rather than issuing a
     * second GET, which is also what keeps `loading` honest.
     *
     * Epoch-guarded: a read that resolves after `reset` commits nothing at
     * all, neither status nor error, because the member it was asked for is
     * gone. Its failure goes to `readError`, never to `state.error`.
     */
    async refreshStatus(context) {
      const { commit, rootState, state } = context;
      const live = inFlightReads.get(state);
      if (live && live.epoch === state.epoch) return live.read;

      const { epoch } = state;
      const auth = authFor(rootState, state);
      commit('setLoading', true);
      // The entry is registered BEFORE the request starts and compared by
      // identity, so a client that throws synchronously cannot reach the
      // `finally` before there is an entry to clear.
      const entry = { epoch, read: null };
      inFlightReads.set(state, entry);
      entry.read = (async () => {
        try {
          const status = await fetchEncryptionStatus(auth);
          if (state.epoch === epoch) {
            commit('setStatus', status);
            commit('setReadError', null);
          }
          return status;
        } catch (error) {
          if (state.epoch === epoch) commit('setReadError', errorSnapshot(error));
          throw error;
        } finally {
          if (inFlightReads.get(state) === entry) inFlightReads.delete(state);
          if (state.epoch === epoch) commit('setLoading', false);
        }
      })();
      return entry.read;
    },

    /**
     * Register `state.localDevice`.
     *
     * With a `signer` the registration is signed over `{ deviceId, publicKey }`
     * and the API creates the device APPROVED — the restored-platform-copy case,
     * where `courses-keys` came up with `needsRegistration` true. `selfWrapped`
     * is MK wrapped to this device's own public key, so the device can read its
     * key back on the next launch from the server rather than the platform.
     *
     * Without one the device is PENDING and waits for an approved device.
     *
     * A status refresh always follows, because `register` answers the member's
     * own id with the existing row: the reply may be older than what was asked
     * for, and the screens must render what the server actually holds.
     *
     * @param {{ selfWrapped?: object, signer?: Function }} [options]
     */
    async registerThisDevice(context, { selfWrapped, signer } = {}) {
      const { dispatch, rootState, state } = context;
      const device = state.localDevice;
      if (!device?.id) {
        refuse(
          context,
          'courses-devices/registerThisDevice: no local device identity — dispatch setLocalDevice first',
        );
      }
      if (selfWrapped && typeof signer !== 'function') {
        // DeviceService.register refuses this with a 403; failing here says so
        // without spending a request on a refusal the caller could have known.
        refuse(
          context,
          'courses-devices/registerThisDevice: a selfWrapped registration needs a signer',
        );
      }
      const auth = authFor(rootState, state);
      const body = { ...device, ...(selfWrapped ? { selfWrapped } : {}) };
      const registered = signer
        ? await signed(
            context,
            {
              op: 'register',
              // The signer hashes the wrapping into the message (api#1092);
              // a signature that leaves it out is refused as invalid.
              params: {
                deviceId: device.id,
                publicKey: device.publicKey,
                ...(selfWrapped ? { selfWrapped } : {}),
              },
              signer,
            },
            (signature, nonce) => registerDevice(auth, { ...body, signature, nonce }),
          )
        : await tracked(context, () => registerDevice(auth, body));
      await dispatch('refreshStatus').catch(() => {});
      return registered;
    },

    /**
     * Approve a PENDING device with MK wrapped to its public key. Signed over
     * `{ deviceId, wrappedMkHash }`.
     *
     * `wrappedMk` is the caller's: it comes from
     * `wrapMasterKeyToDevice({ mk, keyId, memberId, devicePublicKey })` against
     * the pending device's registered public key, and the signature must cover
     * `wrappedMkHash(wrappedMk)` so a captured one cannot be replayed against a
     * different wrapping.
     *
     * @param {{ id: string, wrappedMk: object, signer: Function }} args
     */
    async approveDevice(context, { id, wrappedMk, signer }) {
      if (!wrappedMk) {
        refuse(context, 'courses-devices/approveDevice: wrappedMk is required');
      }
      const auth = authFor(context.rootState, context.state);
      // Read BEFORE the approval: the refresh below replaces the status, and
      // an approved device is no longer the pending one this measures.
      const requestedAt = context.getters.devices?.find(device => device.id === id)?.createdAt;
      const approved = await signed(
        context,
        { op: 'approve', params: { deviceId: id, wrappedMk }, signer },
        (signature, nonce) => approveDeviceRequest(auth, id, { wrappedMk, signature, nonce }),
      );
      trackElapsedSince('next_steps_device_request_approved', requestedAt);
      await context.dispatch('refreshStatus').catch(() => {});
      return approved;
    },

    /**
     * Refuse a PENDING device.
     *
     * Unsigned: the deployed controller puts no `SignedOperationGuard` on
     * `PUT /devices/:id/deny`, so there is no signature to make and no nonce to
     * spend. The spec's "Deny signs a deny" is not what shipped, and the
     * contract wins — denying only ever revokes a device that has no key yet,
     * which destroys nothing.
     *
     * @param {{ id: string }} args
     */
    async denyDevice(context, { id }) {
      const auth = authFor(context.rootState, context.state);
      const denied = await tracked(context, () => denyDeviceRequest(auth, id));
      await context.dispatch('refreshStatus').catch(() => {});
      return denied;
    },

    /**
     * Revoke an APPROVED device, clearing its wrapping. Signed over
     * `{ deviceId }`.
     *
     * @param {{ id: string, signer: Function }} args
     */
    async revokeDevice(context, { id, signer }) {
      const auth = authFor(context.rootState, context.state);
      const revoked = await signed(
        context,
        { op: 'revoke', params: { deviceId: id }, signer },
        (signature, nonce) => revokeDeviceRequest(auth, id, { signature, nonce }),
      );
      await context.dispatch('refreshStatus').catch(() => {});
      return revoked;
    },

    /**
     * Set or replace the recovery wrapping. Signed over
     * `{ recoveryWrappingHash, expectedVersion }`; the API compare-and-sets on
     * `recoveryVersion` and answers 409 when another device moved it first.
     *
     * @param {{ recoveryWrapping: object, expectedVersion: number, signer: Function }} args
     */
    async setRecovery(context, { recoveryWrapping, expectedVersion, signer }) {
      const auth = authFor(context.rootState, context.state);
      const bundle = await signed(
        context,
        { op: 'recovery', params: { recoveryWrapping, expectedVersion }, signer },
        (signature, nonce) =>
          setRecoveryRequest(auth, { recoveryWrapping, expectedVersion, signature, nonce }),
      );
      await context.dispatch('refreshStatus').catch(() => {});
      return bundle;
    },

    /**
     * Set or replace the optional passphrase (#1695, api#1125). Signed over
     * `{ passphraseWrappingHash, expectedVersion }`; compare-and-set on
     * `passphraseVersion`, so a 409 means another device wrote one first.
     *
     * The store never sees the passphrase — only the wrapping already made
     * under a key derived from it, by `wrapMasterKeyUnderPassphrase`. Nothing
     * here can, or should, be able to reconstruct it.
     *
     * @param {{ passphraseWrapping: object, expectedVersion: number, signer: Function }} args
     */
    async setPassphrase(context, { passphraseWrapping, expectedVersion, signer }) {
      const auth = authFor(context.rootState, context.state);
      const bundle = await signed(
        context,
        { op: 'passphrase', params: { passphraseWrapping, expectedVersion }, signer },
        (signature, nonce) =>
          setPassphraseRequest(auth, { passphraseWrapping, expectedVersion, signature, nonce }),
      );
      await context.dispatch('refreshStatus').catch(() => {});
      return bundle;
    },

    /**
     * Remove the passphrase (#1695, api#1125).
     *
     * A DIFFERENT op from setting one — `passphrase-remove` — so a removal
     * signature can never be presented as a set. Version-checked for the
     * sharper reason: a removal races a set, and without the check a device
     * removing the passphrase it read a moment ago would silently delete one
     * another device had just written.
     *
     * @param {{ expectedVersion: number, signer: Function }} args
     */
    async removePassphrase(context, { expectedVersion, signer }) {
      const auth = authFor(context.rootState, context.state);
      const bundle = await signed(
        context,
        { op: 'passphrase-remove', params: { expectedVersion }, signer },
        (signature, nonce) => removePassphraseRequest(auth, { expectedVersion, signature, nonce }),
      );
      await context.dispatch('refreshStatus').catch(() => {});
      return bundle;
    },

    /**
     * Open a rotation (#1694, spec 6.12 step 1).
     *
     * The whole of the next generation in one body: a begin that stored the
     * key id first and its wrappings afterwards would have a window in which
     * the bundle names a generation nothing can open, and a device that died
     * there could sign neither commit nor abort.
     *
     * @param {object} args the pending generation, the device, and a signer
     */
    async beginRotation(context, { signer, ...pending }) {
      const auth = authFor(context.rootState, context.state);
      const bundle = await signed(
        context,
        { op: 'rotate-begin', params: pending, signer },
        (signature, nonce) => rotateBeginRequest(auth, { ...pending, signature, nonce }),
      );
      await context.dispatch('refreshStatus').catch(() => {});
      return bundle;
    },

    /**
     * ⚠️ Commit a rotation (spec 6.12 step 3). THE DESTRUCTIVE ONE: it revokes
     * every device except the one that rotated and clears their `wrappedMk`.
     * That is "sign out all other devices", and it cannot be undone.
     *
     * Signed by the PENDING key, so the caller must build that signer — the
     * ordinary one would be refused by a guard that verifies against the
     * pending bundle key.
     *
     * @param {{ pendingKeyId: string, signer: Function }} args
     */
    async commitRotation(context, { pendingKeyId, signer, keep = [] }) {
      const auth = authFor(context.rootState, context.state);
      const bundle = await signed(
        context,
        { op: 'rotate-commit', params: { pendingKeyId, keep }, signer },
        (signature, nonce) => rotateCommitRequest(auth, { pendingKeyId, keep, signature, nonce }),
      );
      await context.dispatch('refreshStatus').catch(() => {});
      return bundle;
    },

    /**
     * Abort a rotation (spec 6.12 step 4). Nothing is revoked and no version
     * moves; a member who changes their mind should not pay for it.
     *
     * A DIFFERENT op from commit, signed by the same key over the same field —
     * which is exactly why the names must differ (decision 49).
     *
     * @param {{ pendingKeyId: string, signer: Function }} args
     */
    /**
     * Enter the ABORTING state (api#1148): from here the API accepts writes on
     * the COMMITTED generation and refuses the pending one, which is what lets
     * the walk run backwards. Signed by the pending key under its own op.
     *
     * @param {{ pendingKeyId: string, signer: Function }} args
     */
    async abortBeginRotation(context, { pendingKeyId, signer }) {
      const auth = authFor(context.rootState, context.state);
      const bundle = await signed(
        context,
        { op: 'rotate-abort-begin', params: { pendingKeyId }, signer },
        (signature, nonce) => rotateAbortBeginRequest(auth, { pendingKeyId, signature, nonce }),
      );
      // The status is what every other decision reads the direction from, and
      // the walk asks immediately after this — a stale one would send it the
      // wrong way.
      await context.dispatch('refreshStatus').catch(() => {});
      return bundle;
    },

    async abortRotation(context, { pendingKeyId, signer }) {
      const auth = authFor(context.rootState, context.state);
      const bundle = await signed(
        context,
        { op: 'rotate-abort', params: { pendingKeyId }, signer },
        (signature, nonce) => rotateAbortRequest(auth, { pendingKeyId, signature, nonce }),
      );
      await context.dispatch('refreshStatus').catch(() => {});
      return bundle;
    },

    /**
     * One page of the member's rows still sealed under `kid` (#1694, api#1128).
     *
     * A read, so it never touches `state.error`: the walk reports its own
     * progress and a failed page is retried on the next pass rather than
     * shown to the member as a fault.
     *
     * @param {{ kid: string, cursor?: string, limit?: number }} args
     */
    rotationRows(context, { kid, cursor, limit } = {}) {
      const auth = authFor(context.rootState, context.state);
      return fetchRotationRows(auth, { kid, cursor, limit });
    },

    /**
     * "Start over". With a `signer` the reset is immediate and proven; without
     * one the API starts a seven-day hold, which is how a member who has lost
     * every device asks — and how an approved device gets the chance to cancel.
     *
     * @param {{ signer?: Function }} [options]
     */
    async requestReset(context, { signer } = {}) {
      const auth = authFor(context.rootState, context.state);
      const result = signer
        ? await signed(context, { op: 'reset', params: {}, signer }, (signature, nonce) =>
            requestResetRequest(auth, { signature, nonce }),
          )
        : await tracked(context, () => requestResetRequest(auth, {}));
      await context.dispatch('refreshStatus').catch(() => {});
      return result;
    },

    /**
     * Cancel a pending hold. Signed over nothing but the operation, so a
     * session thief cannot call off a genuine reset.
     *
     * @param {{ signer: Function }} args
     */
    async cancelReset(context, { signer }) {
      const auth = authFor(context.rootState, context.state);
      const requestedAt = context.getters.pendingReset?.requestedAt;
      const bundle = await signed(
        context,
        { op: 'cancel', params: {}, signer },
        (signature, nonce) => cancelResetRequest(auth, { signature, nonce }),
      );
      trackElapsedSince('next_steps_reset_hold_cancelled', requestedAt);
      await context.dispatch('refreshStatus').catch(() => {});
      return bundle;
    },

    /**
     * Assert that no plaintext rows remain. Session-only; the server counts and
     * answers 409 with a per-table count if any are left.
     */
    async markMigrated(context) {
      const auth = authFor(context.rootState, context.state);
      const bundle = await tracked(context, () => markMigratedRequest(auth));
      await context.dispatch('refreshStatus').catch(() => {});
      return bundle;
    },

    /**
     * Refresh the status now and then every `intervalMs` until `until(getters)`
     * holds or `stopPolling` is dispatched. This is how the waiting device picks
     * up its wrapped key without a reload.
     *
     * Single-flight: a tick that lands while a refresh is still in the air is
     * dropped, not queued, so a slow network cannot stack requests. A failed
     * refresh is recorded by `refreshStatus` in `readError` and does not end
     * the wait — the device is often on a network that drops one request in
     * ten, and one failure must not leave the member staring at a screen that
     * has stopped asking.
     *
     * Every tick checks that the timer it belongs to is still THIS module's
     * timer. A member who leaves the screen and comes back installs a second
     * poll while the first one's slow request is still out; without the check
     * that abandoned tick's `until` dispatches the module-wide `stopPolling`
     * and kills the poll now on display, which then waits for ever with no
     * spinner for a key it will never ask for again.
     *
     * `loading` covers reads, and so covers these ticks; the mutating actions
     * do not set it. Both screens take `busy` from their caller for that.
     *
     * @param {{ intervalMs?: number, until?: (getters: object) => boolean }} [options]
     */
    async startPolling(context, { intervalMs = DEFAULT_POLL_INTERVAL_MS, until } = {}) {
      const { commit, dispatch, getters, state } = context;
      if (state.polling) return;
      commit('setPolling', true);

      let inFlight = false;
      let myTimer = null;
      const mine = () => state.pollTimer === myTimer;
      const poll = async () => {
        if (inFlight || !mine()) return;
        inFlight = true;
        try {
          await dispatch('refreshStatus');
          if (typeof until === 'function' && until(getters) && mine()) {
            await dispatch('stopPolling');
          }
        } catch (error) {
          // Recorded in state.readError by refreshStatus; the wait continues.
        } finally {
          inFlight = false;
        }
      };

      myTimer = setInterval(poll, intervalMs);
      commit('setPollTimer', myTimer);
      // Ask once immediately: a member who has just been approved should not
      // wait out a full interval to find out.
      await poll();
    },

    /** The Devices screen's "Review": show this request again, "Not now" or not. */
    requestReview({ commit }, id) {
      commit('setReviewRequest', id);
    },
    stopPolling({ commit, state }) {
      if (state.pollTimer !== null) clearInterval(state.pollTimer);
      commit('setPollTimer', null);
      commit('setPolling', false);
    },

    /** For callers that only dispatch; the mutation is what logout commits. */
    reset({ commit }) {
      commit('reset');
    },
  },
};
